import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box, ClickAwayListener } from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { CompanyServiceReport } from "../../api/serviceReportsApi";
import serviceReportsApi from "../../api/serviceReportsApi";
import { ServiceReport, ServiceReportFile, MachinePartGroup } from "../../../machinery/models/ServiceReportsModel";
import { WorkOrderDetail, LinkedServiceReport } from "../../models/models";
import { formatFileSize } from "../../../../shared/models/models";
import { ImagePreviewStrip } from "../../../../shared/components/gallery";
import { useGalleryImages } from "../../../../shared/components/gallery/useGalleryImages";
import customerMachineServiceReportsApi from "../../../machinery/api/customerMachineServiceReportsApi";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

// ─── Locked palette (matches WorkOrderDetailPage) ───────────
const p = {
  terracotta: "#D4572B",
  amber: "#E8A817",
  charcoal: "#0C1117",
  warmCream: "#F7F5F0",
  pageBg: "#F2F0EB",
  white: "#FFFFFF",
  border: "#E8E5DF",
  muted: "#8C9099",
  body: "#4A4F57",
  green: "#2D8C4E",
  blue: "#3B82F6",
  red: "#E74C3C",
};

const cardSx = {
  backgroundColor: p.white,
  borderRadius: "12px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

// ─── Urgency badge config ──────────────────────────────────
// Tab badges: small colored dot only
// Content badges: pill with text — CRITICAL is solid, others are tinted bg + dark text
const URGENCY_MAP: Record<number, { dotBg: string; pillBg: string; pillColor: string; labelKey: string }> = {
  1: { dotBg: "#DC2626", pillBg: "#DC2626", pillColor: "#FFFFFF", labelKey: "priorities.critical" },
  2: { dotBg: "#E8A817", pillBg: "#FEF3C7", pillColor: "#92400E", labelKey: "priorities.high" },
  4: { dotBg: "#3B82F6", pillBg: "#DCFCE7", pillColor: "#166534", labelKey: "priorities.normal" },
  0: { dotBg: "#8C9099", pillBg: "#F3F4F6", pillColor: "#6B7280", labelKey: "priorities.low" },
};

// ─── Sub-section label (AFFECTED PART, PROBLEM DESCRIPTION, etc.) ───
const SubSectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{
    fontFamily: dt.fontMono,
    fontSize: "10px",
    fontWeight: 600,
    color: p.muted,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  }}>
    {children}
  </Box>
);

// ─── Urgency Badge (content area — tinted pill) ─────────────
const UrgencyBadge = ({ urgency }: { urgency?: number }) => {
  const { translate } = useTranslation("operations");
  const u = URGENCY_MAP[urgency ?? 0] || URGENCY_MAP[0];
  return (
    <Box sx={{
      display: "inline-flex",
      alignItems: "center",
      borderRadius: "100px",
      padding: "3px 8px",
      fontSize: "10px",
      fontWeight: 600,
      fontFamily: dt.fontMono,
      color: u.pillColor,
      backgroundColor: u.pillBg,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      lineHeight: "12px",
    }}>
      {translate(u.labelKey)}
    </Box>
  );
};

// ─── Urgency Dot (for tabs) ─────────────────────────────────
const UrgencyDot = ({ urgency }: { urgency?: number }) => {
  const u = URGENCY_MAP[urgency ?? 0] || URGENCY_MAP[0];
  return (
    <Box sx={{
      width: "8px",
      height: "8px",
      minWidth: "8px",
      borderRadius: "50%",
      backgroundColor: u.dotBg,
      flexShrink: 0,
    }} />
  );
};

// ─── Tab Urgency Badge (small pill in tab, like in JWB-0 design) ────
const TabUrgencyBadge = ({ urgency }: { urgency?: number }) => {
  const { translate } = useTranslation("operations");
  const u = URGENCY_MAP[urgency ?? 0] || URGENCY_MAP[0];
  return (
    <Box sx={{
      display: "inline-flex",
      alignItems: "center",
      borderRadius: "100px",
      padding: "2px 6px",
      fontSize: "9px",
      fontWeight: 600,
      fontFamily: dt.fontMono,
      color: u.pillColor,
      backgroundColor: u.pillBg,
      lineHeight: "12px",
      textTransform: "uppercase",
    }}>
      {translate(u.labelKey)}
    </Box>
  );
};

// ─── Image extension checker ────────────────────────────────
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isImageFile = (name: string): boolean => IMAGE_EXTENSIONS.test(name);
const isBase64Image = (content: string): boolean => content.startsWith("data:image/");

// ─── File attachment sub-component with gallery ─────────────
const SrAttachments = ({ files, translate }: { files: ServiceReportFile[]; translate: (key: string) => string }) => {
  // Split files into images and non-images
  const imageFiles = useMemo(() =>
    files.filter(f => {
      if (f.content && isBase64Image(f.content)) return true;
      if (f.name && isImageFile(f.name)) return true;
      return false;
    }),
    [files]
  );

  const nonImageFiles = useMemo(() =>
    files.filter(f => {
      if (f.content && isBase64Image(f.content)) return false;
      if (f.name && isImageFile(f.name)) return false;
      return true;
    }),
    [files]
  );

  // Convert image files to GalleryImage format
  const galleryImages = useGalleryImages({
    type: "entityFiles",
    files: imageFiles.filter(f => f.content && f.name) as Array<{ name: string; content: string; size?: number }>,
  });

  if (files.length === 0) return null;

  return (
    <Box>
      <SubSectionLabel>{translate("detail.srAttachments")}</SubSectionLabel>

      {/* Image thumbnails via gallery strip */}
      {galleryImages.length > 0 && (
        <Box sx={{ mt: "8px", mb: nonImageFiles.length > 0 ? "10px" : 0 }}>
          <ImagePreviewStrip
            images={galleryImages}
            maxVisible={3}
            thumbnailSize={56}
            gap={8}
            borderRadius="6px"
          />
        </Box>
      )}

      {/* Non-image file chips */}
      {nonImageFiles.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "8px" }}>
          {nonImageFiles.map((f: ServiceReportFile, idx: number) => (
            <Box key={idx} sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              backgroundColor: p.warmCream,
              border: `1px solid ${p.border}`,
              borderRadius: "8px",
              fontSize: "12px",
              fontFamily: dt.font,
              color: p.charcoal,
            }}>
              <Box sx={{ fontWeight: 400 }}>{f.name}</Box>
              {f.size > 0 && (
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "10px", color: p.muted, lineHeight: "12px" }}>
                  {formatFileSize(f.size)}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Max visible tabs constant ──────────────────────────────
// Desktop/tablet: show first 2 SR tabs expanded + overflow dropdown.
// Phone: show 0 tabs — all SRs live behind the dropdown pill for vertical space.
const MAX_VISIBLE_TABS_DESKTOP = 2;
const MAX_VISIBLE_TABS_PHONE = 0;

interface IncidentContextCardProps {
  wo: WorkOrderDetail;
  translate: (key: string) => string;
  onViewFullReport?: (srId: string, srNumber?: string) => void;
}

const IncidentContextCard: React.FC<IncidentContextCardProps> = ({
  wo,
  translate,
  onViewFullReport,
}) => {
  const { isPhone } = useBreakpoint();
  const maxVisibleTabs = isPhone ? MAX_VISIBLE_TABS_PHONE : MAX_VISIBLE_TABS_DESKTOP;
  // Collect all linked SR IDs (junction table + legacy single field)
  const allLinkedSrs: LinkedServiceReport[] = React.useMemo(() => {
    const junctionSrs = wo.linkedServiceReports || [];
    if (
      wo.serviceReportId &&
      !junctionSrs.some((l) => l.serviceReportId === wo.serviceReportId)
    ) {
      return [
        ...junctionSrs,
        {
          serviceReportId: wo.serviceReportId,
          serviceReportNumber: undefined,
          linkedDate: wo.createdDate,
        },
      ];
    }
    return junctionSrs;
  }, [wo.linkedServiceReports, wo.serviceReportId, wo.createdDate]);

  const hasSrs = allLinkedSrs.length > 0;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [companySrs, setCompanySrs] = useState<CompanyServiceReport[]>([]);
  const [detailCache, setDetailCache] = useState<Map<string, ServiceReport>>(new Map());
  const [machineParts, setMachineParts] = useState<MachinePartGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownAnchorRef = useRef<HTMLDivElement>(null);

  // Fetch company SRs (metadata) on mount — only runs when SRs exist
  useEffect(() => {
    if (!hasSrs) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const all = await serviceReportsApi.getCompanyServiceReports();
        if (!cancelled) {
          const srIds = new Set(allLinkedSrs.map((l) => l.serviceReportId));
          setCompanySrs(all.filter((sr) => srIds.has(sr.id)));
        }
      } catch {
        if (!cancelled) setCompanySrs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [allLinkedSrs, hasSrs]);

  // Fetch machine parts for affected part name resolution
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const parts = await customerMachineServiceReportsApi.getMachineParts();
        if (!cancelled) setMachineParts(parts);
      } catch {
        // silently fail
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // When active tab changes, fetch detail (files) for that SR if not cached
  const activeSr = companySrs[activeTabIndex] || companySrs[0];
  const fetchDetail = useCallback(async (sr: CompanyServiceReport) => {
    if (!sr?.machineId || !sr?.number) return;
    if (detailCache.has(sr.id)) return;
    try {
      const detail = await serviceReportsApi.getServiceReportDetail(sr.machineId, sr.number);
      if (detail) {
        setDetailCache((prev) => new Map(prev).set(sr.id, detail));
      }
    } catch {
      // silently fail — files won't be available
    }
  }, [detailCache]);

  useEffect(() => {
    if (activeSr) {
      fetchDetail(activeSr);
    }
  }, [activeSr, fetchDetail]);

  const activeDetail = activeSr ? detailCache.get(activeSr.id) : undefined;

  // Resolve affected part name from machine parts
  const affectedPartName = useMemo(() => {
    if (!activeDetail?.affectedPartCategoryId || !machineParts.length) return undefined;
    for (const group of machineParts) {
      const part = group.parts?.find((pt) => pt.id === activeDetail.affectedPartCategoryId);
      if (part) return part.name;
    }
    return undefined;
  }, [machineParts, activeDetail?.affectedPartCategoryId]);

  // Tab management: first N visible, rest in dropdown (N = 0 on phone, 2 otherwise)
  const visibleTabs = useMemo(() => companySrs.slice(0, maxVisibleTabs), [companySrs, maxVisibleTabs]);
  const overflowTabs = useMemo(() => companySrs.slice(maxVisibleTabs), [companySrs, maxVisibleTabs]);
  const isOverflowActive = activeTabIndex >= maxVisibleTabs;

  const handleTabClick = useCallback((idx: number) => {
    setActiveTabIndex(idx);
    setDropdownOpen(false);
  }, []);

  const handleDropdownToggle = useCallback(() => {
    setDropdownOpen((prev) => !prev);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  // Don't render if no SRs linked
  if (!hasSrs) return null;

  // If still loading, show skeleton
  if (loading) {
    return (
      <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.1s both" }}>
        <Box sx={{ padding: "20px 24px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", pb: "14px" }}>
            <Box sx={{
              fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 600,
              color: p.muted, textTransform: "uppercase", letterSpacing: "0.8px",
            }}>
              {translate("detail.incidentContext")}
            </Box>
            <Box sx={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              minWidth: "20px", height: "18px", borderRadius: "100px",
              backgroundColor: p.muted, fontSize: "11px", fontWeight: 600,
              fontFamily: dt.fontMono, color: "#fff", padding: "1px 7px",
            }}>
              {allLinkedSrs.length}
            </Box>
          </Box>
          <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: p.muted, padding: "20px 0" }}>
            {translate("detail.loadingServiceReport")}
          </Box>
        </Box>
      </Box>
    );
  }

  if (companySrs.length === 0) {
    return null; // SRs from company endpoint didn't match — hide
  }

  return (
    <Box sx={{ ...cardSx, animation: "cardReveal 0.4s ease 0.1s both" }}>
      {/* Header area: INCIDENT CONTEXT + count badge + tab bar */}
      <Box sx={{ padding: "20px 24px 0 24px" }}>
        {/* Section header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", pb: "14px" }}>
          <Box sx={{
            fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 600,
            color: p.muted, textTransform: "uppercase", letterSpacing: "0.8px",
          }}>
            {translate("detail.incidentContext")}
          </Box>
          <Box sx={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            minWidth: "20px", height: "18px", borderRadius: "100px",
            backgroundColor: p.muted, fontSize: "11px", fontWeight: 600,
            fontFamily: dt.fontMono, color: "#fff", padding: "1px 7px",
          }}>
            {companySrs.length}
          </Box>
        </Box>

        {/* Tab bar — visible tabs + "+N more" dropdown.
            On phone maxVisibleTabs = 0, so only the dropdown pill renders. */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "0",
          borderBottom: isPhone ? "none" : `1px solid ${p.border}`,
          paddingInline: "4px",
          paddingBottom: isPhone ? "12px" : 0,
        }}>
          {visibleTabs.map((sr, idx) => {
            const isActive = idx === activeTabIndex;
            const shortTitle = sr.title && sr.title.length > 18
              ? sr.title.substring(0, 18) + "..."
              : (sr.title || translate("detail.untitled"));
            return (
              <Box
                key={sr.id}
                onClick={() => handleTabClick(idx)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 16px",
                  cursor: "pointer",
                  marginBottom: "-1px",
                  borderBottom: isActive ? `2px solid ${p.terracotta}` : "2px solid transparent",
                  transition: "all 0.15s ease",
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: isActive ? "transparent" : "rgba(0,0,0,0.02)",
                  },
                }}
              >
                <Box sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "12px",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? p.terracotta : p.body,
                  whiteSpace: "nowrap",
                  lineHeight: "16px",
                }}>
                  {sr.number || `SR-${sr.id.substring(0, 5).toUpperCase()}`}
                </Box>
                <Box sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 400,
                  color: isActive ? p.charcoal : p.muted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "130px",
                  lineHeight: "16px",
                }}>
                  {shortTitle}
                </Box>
                {isActive ? (
                  <TabUrgencyBadge urgency={sr.urgency} />
                ) : (
                  <UrgencyDot urgency={sr.urgency} />
                )}
              </Box>
            );
          })}

          {/* "+N more" dropdown pill.
              On phone: full-width, shows active SR number + truncated title (no tabs above). */}
          {overflowTabs.length > 0 && (
            <Box sx={{ position: "relative", marginLeft: isPhone ? 0 : "4px", width: isPhone ? "100%" : "auto" }} ref={dropdownAnchorRef}>
              <Box
                onClick={handleDropdownToggle}
                sx={{
                  display: isPhone ? "flex" : "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: isPhone ? "10px 14px" : "6px 12px",
                  borderRadius: isPhone ? "10px" : "20px",
                  cursor: "pointer",
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: isOverflowActive ? p.charcoal : p.body,
                  backgroundColor: isPhone ? p.warmCream : (isOverflowActive ? p.warmCream : p.pageBg),
                  border: isPhone
                    ? `1px solid ${p.border}`
                    : (isOverflowActive ? `1px solid ${p.border}` : "1px solid transparent"),
                  transition: "all 0.15s ease",
                  whiteSpace: "nowrap",
                  lineHeight: "16px",
                  width: isPhone ? "100%" : "auto",
                  justifyContent: isPhone ? "space-between" : "flex-start",
                  "&:hover": {
                    backgroundColor: p.warmCream,
                    border: `1px solid ${p.border}`,
                  },
                }}
              >
                {isPhone && isOverflowActive ? (
                  // Phone dropdown-only mode: show SR-number + truncated title in the pill
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
                    <UrgencyDot urgency={companySrs[activeTabIndex]?.urgency} />
                    <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", fontWeight: 600, color: p.charcoal, flexShrink: 0 }}>
                      {companySrs[activeTabIndex]?.number || `SR-${companySrs[activeTabIndex]?.id.substring(0, 5).toUpperCase()}`}
                    </Box>
                    <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: p.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
                      {companySrs[activeTabIndex]?.title || translate("detail.untitled")}
                    </Box>
                  </Box>
                ) : (
                  isOverflowActive
                    ? (companySrs[activeTabIndex]?.number || `SR-${companySrs[activeTabIndex]?.id.substring(0, 5).toUpperCase()}`)
                    : translate("detail.plusMore").replace("{{count}}", String(overflowTabs.length))
                )}
                <ArrowDownIcon sx={{
                  fontSize: 14,
                  color: isOverflowActive ? p.charcoal : p.body,
                  transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.15s ease",
                }} />
              </Box>

              {/* Dropdown menu */}
              {dropdownOpen && (
                <ClickAwayListener onClickAway={handleDropdownClose}>
                  <Box sx={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    left: isPhone ? 0 : "auto",
                    zIndex: 10,
                    marginTop: "4px",
                    minWidth: isPhone ? "auto" : "260px",
                    width: isPhone ? "100%" : "auto",
                    backgroundColor: p.white,
                    border: `1px solid ${p.border}`,
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                  }}>
                    {overflowTabs.map((sr, overflowIdx) => {
                      const realIdx = maxVisibleTabs + overflowIdx;
                      const isActiveItem = realIdx === activeTabIndex;
                      return (
                        <Box
                          key={sr.id}
                          onClick={() => handleTabClick(realIdx)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 14px",
                            cursor: "pointer",
                            backgroundColor: isActiveItem ? p.warmCream : "transparent",
                            transition: "background-color 0.1s ease",
                            "&:hover": {
                              backgroundColor: isActiveItem ? p.warmCream : "rgba(0,0,0,0.02)",
                            },
                          }}
                        >
                          <UrgencyDot urgency={sr.urgency} />
                          <Box sx={{
                            fontFamily: dt.fontMono,
                            fontSize: "12px",
                            fontWeight: isActiveItem ? 600 : 400,
                            color: isActiveItem ? p.charcoal : p.body,
                          }}>
                            {sr.number || `SR-${sr.id.substring(0, 5).toUpperCase()}`}
                          </Box>
                          <Box sx={{
                            fontFamily: dt.font,
                            fontSize: "12px",
                            color: p.muted,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}>
                            {sr.title && sr.title.length > 25
                              ? sr.title.substring(0, 25) + "..."
                              : (sr.title || translate("detail.untitled"))}
                          </Box>
                          <TabUrgencyBadge urgency={sr.urgency} />
                        </Box>
                      );
                    })}
                  </Box>
                </ClickAwayListener>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Active tab content */}
      {activeSr && (
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          padding: "20px 24px 24px 24px",
        }}>
          {/* SR number pill + title + urgency badge + alarm code */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {/* Dark pill for SR number */}
            <Box sx={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: p.charcoal,
              borderRadius: "100px",
              padding: "3px 10px",
              fontFamily: dt.fontMono,
              fontSize: "12px",
              fontWeight: 600,
              color: "#fff",
              lineHeight: "16px",
            }}>
              {activeSr.number || `SR-${activeSr.id.substring(0, 5).toUpperCase()}`}
            </Box>
            <Box sx={{
              fontFamily: dt.font,
              fontSize: "15px",
              fontWeight: 600,
              color: p.charcoal,
              lineHeight: "18px",
              flex: 1,
              minWidth: 0,
            }}>
              {activeSr.title || translate("detail.untitled")}
            </Box>
            <UrgencyBadge urgency={activeSr.urgency} />
            {activeSr.alarmCode && (
              <Box sx={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "6px",
                padding: "3px 8px",
                backgroundColor: p.warmCream,
                fontFamily: dt.fontMono,
                fontSize: "11px",
                fontWeight: 400,
                color: p.muted,
                lineHeight: "14px",
              }}>
                {activeSr.alarmCode}
              </Box>
            )}
          </Box>

          {/* Affected Part — resolved via machine parts lookup */}
          {activeDetail?.affectedPartCategoryId && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <SubSectionLabel>{translate("detail.affectedPart")}</SubSectionLabel>
              <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: p.body, lineHeight: "16px" }}>
                {affectedPartName || translate("detail.unknownPart")}
              </Box>
            </Box>
          )}

          {/* PROBLEM DESCRIPTION */}
          {activeSr.description && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <SubSectionLabel>{translate("detail.problemDescription")}</SubSectionLabel>
              <Box sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                color: p.body,
                lineHeight: "22px",
                whiteSpace: "pre-wrap",
              }}>
                {activeSr.description}
              </Box>
            </Box>
          )}

          {/* TECHNICAL NOTES */}
          {(activeSr as any).notes && (
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              backgroundColor: p.warmCream,
              borderRadius: "8px",
              padding: "14px 16px",
            }}>
              <SubSectionLabel>{translate("detail.technicalNotes")}</SubSectionLabel>
              <Box sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                color: p.body,
                lineHeight: "20px",
                whiteSpace: "pre-wrap",
              }}>
                {(activeSr as any).notes}
              </Box>
            </Box>
          )}

          {/* ATTACHMENTS — images via gallery, non-images as chips */}
          {activeDetail && activeDetail.files && activeDetail.files.length > 0 && (
            <SrAttachments files={activeDetail.files} translate={translate} />
          )}

          {/* Reported by attribution */}
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            paddingTop: "4px",
            borderTop: `1px solid ${p.border}`,
          }}>
            <Box sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 400,
              color: p.muted,
              lineHeight: "14px",
            }}>
              {translate("detail.reportedBy")}
            </Box>
            <Box sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 500,
              color: p.body,
              lineHeight: "14px",
            }}>
              {(activeSr as any).createdByName || translate("serviceReports.unknownReporter")}
            </Box>
            <Box sx={{
              fontFamily: dt.fontMono,
              fontSize: "11px",
              fontWeight: 400,
              color: p.muted,
              lineHeight: "14px",
            }}>
              &middot;{" "}
              {activeSr.createdDate
                ? formatUiDate(activeSr.createdDate)
                : activeSr.openDate
                  ? formatUiDate(activeSr.openDate as any)
                  : ""}
            </Box>
            {/* Spacer to push "View Full Report" to the right */}
            <Box sx={{ flex: 1 }} />
            {/* View Full Report button */}
            {onViewFullReport && (
              <Box
                onClick={() => onViewFullReport(activeSr.id, activeSr.number)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 500,
                  color: p.blue,
                  cursor: "pointer",
                  lineHeight: "14px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {translate("detail.viewFullReport")}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default IncidentContextCard;
